/**
 * QueueHealthPanel — Mission Control extension for M8.
 *
 * Renders queue health metrics + failed/dead-letter jobs with retry & cancel.
 */
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { PlayCircle, PauseCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import {
  JOB_STATUS_TONES, cancelJob, getQueueHealth, listJobs, retryJob,
  type PublishingJob, type QueueHealth,
} from "@/entities/publishing";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

const fmtMs = (v: number): string => {
  if (!v) return "—";
  return v < 1000 ? `${Math.round(v)} ms` : `${(v / 1000).toFixed(1)} s`;
};

export function QueueHealthPanel({ workspaceId }: { workspaceId: string }): JSX.Element {
  const qc = useQueryClient();
  const queries = useQueries({
    queries: [
      { queryKey: ["mc", "queue-health", workspaceId], queryFn: () => getQueueHealth(workspaceId), refetchInterval: 5_000 },
      { queryKey: ["mc", "queue-jobs", workspaceId, "failed"], queryFn: () => listJobs(workspaceId, { status: "failed", limit: 10 }), refetchInterval: 5_000 },
      { queryKey: ["mc", "queue-jobs", workspaceId, "dead"], queryFn: () => listJobs(workspaceId, { status: "dead_letter", limit: 10 }), refetchInterval: 5_000 },
      { queryKey: ["mc", "queue-jobs", workspaceId, "running"], queryFn: () => listJobs(workspaceId, { status: "running", limit: 5 }), refetchInterval: 3_000 },
    ],
  });
  const [healthQ, failedQ, deadQ, runningQ] = queries;
  const health = healthQ.data as QueueHealth | undefined;

  const retry = useMutation({
    mutationFn: (jobId: string) => retryJob(workspaceId, jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc"] });
      toast.success("Job re-queued");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const cancel = useMutation({
    mutationFn: (jobId: string) => cancelJob(workspaceId, jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc"] });
      toast.success("Job cancelled");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const jobs: PublishingJob[] = [
    ...((runningQ.data as PublishingJob[] | undefined) ?? []),
    ...((failedQ.data as PublishingJob[] | undefined) ?? []),
    ...((deadQ.data as PublishingJob[] | undefined) ?? []),
  ];

  return (
    <div data-testid="mc-queue-health" className="space-y-3 rounded-lg border border-border/60 bg-card p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Publishing Queue</div>
        <Badge variant="outline" className="font-mono text-[10px] uppercase">
          {health?.success_rate ?? 100}% success · 24h
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Metric label="Queued" value={health?.counts.queued ?? 0} tone="text-sky-600" />
        <Metric label="Running" value={health?.counts.running ?? 0} tone="text-amber-600" />
        <Metric label="Failed" value={health?.counts.failed ?? 0} tone="text-orange-600" />
        <Metric label="Dead" value={health?.counts.dead_letter ?? 0} tone="text-rose-600" />
        <Metric label="p95 latency" value={fmtMs(health?.p95_latency_ms ?? 0)} tone="text-emerald-600" />
        <Metric label="Retries" value={health?.retry_count_total ?? 0} tone="text-fuchsia-600" />
      </div>
      <div className="space-y-1.5">
        {jobs.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/50 px-3 py-4 text-center text-[11px] text-muted-foreground">
            Nothing needs attention.
          </div>
        ) : (
          jobs.slice(0, 8).map((j) => (
            <div
              key={j.id}
              data-testid={`mc-queue-job-${j.id}`}
              className="rounded-md border border-border/50 bg-background/60 px-2 py-1.5 text-[11px]"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge className={`${JOB_STATUS_TONES[j.status]} font-mono text-[9px] uppercase`}>{j.status}</Badge>
                <span className="font-mono text-muted-foreground">{j.channel}</span>
                <span className="font-mono text-muted-foreground">
                  attempt {j.attempts}/{j.max_attempts}
                </span>
                {j.status === "dead_letter" ? (
                  <ShieldAlert className="ml-1 h-3 w-3 text-rose-500" />
                ) : null}
                <div className="ml-auto flex items-center gap-0.5">
                  {(j.status === "dead_letter" || j.status === "failed") ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1.5"
                      data-testid={`mc-queue-retry-${j.id}`}
                      onClick={() => retry.mutate(j.id)}
                    >
                      <PlayCircle className="h-3 w-3" />
                    </Button>
                  ) : null}
                  {j.status !== "cancelled" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1.5"
                      data-testid={`mc-queue-cancel-${j.id}`}
                      onClick={() => cancel.mutate(j.id)}
                    >
                      <PauseCircle className="h-3 w-3" />
                    </Button>
                  ) : null}
                </div>
              </div>
              {j.last_error_message ? (
                <div className="mt-0.5 truncate text-[10px] text-rose-500">
                  {j.last_error_kind}: {j.last_error_message}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: number | string; tone: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border/50 bg-background/60 p-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
